// frontend/modules/customer-category/components/onboarding/SummaryStep.tsx
import React from 'react';
import {
    Descriptions,
    Typography,
    Divider,
    Tag,
    Card,
    Row,
    Col,
    Table,
    Badge
} from 'antd';
import { useTheme } from '@/components/common/ThemeProvider';
import {
    CustomerCategoryDTO,
    CustomerOnboardingData,
    ProductDTO
} from '../../types';

const { Title, Text } = Typography;

interface SummaryStepProps {
    category: CustomerCategoryDTO | null;
    formData: CustomerOnboardingData;
}

const SummaryStep: React.FC<SummaryStepProps> = ({
                                                     category,
                                                     formData
                                                 }) => {
    const { dark } = useTheme();

    // Helper to map product type codes to readable names
    const mapProductType = (type: string): string => {
        switch (type) {
            case 'S': return 'Savings';
            case 'H': return 'Shares';
            case 'F': return 'Fixed Deposit';
            case 'L': return 'Loan';
            default: return type;
        }
    };

    // Determine which customer fields to show in summary
    const getCustomerFields = () => {
        // For summary, we'll only show a subset of important fields
        const fieldsToShow = [
            'name', 'dobirth', 'custSex', 'address', 'phone', 'mobile', 'idCard', 'refNo'
        ];

        const entries = Object.entries(formData.customerData)
            .filter(([key]) => fieldsToShow.includes(key))
            .filter(([_, value]) => value !== undefined && value !== null && value !== '');

        return entries;
    };

    // Determine which additional info fields to show in summary
    const getCustInfoFields = () => {
        // We'll show all non-empty fields
        const entries = Object.entries(formData.custInfoData)
            .filter(([_, value]) => value !== undefined && value !== null && value !== '');

        return entries;
    };

    // Get label for a field
    const getFieldLabel = (fieldName: string, entityType: 'Customer' | 'CustInfo') => {
        if (category) {
            const field = (category.fields || []).find(
                f => f.fieldName === fieldName && f.entityType === entityType
            );

            if (field) {
                return field.customLabel;
            }
        }

        // Fallback to simple formatting
        return fieldName
            .replace(/([A-Z])/g, ' $1') // Insert a space before all capital letters
            .replace(/^./, str => str.toUpperCase()); // Capitalize the first letter
    };

    // Format field value for display
    const formatFieldValue = (value: any, fieldName: string) => {
        if (value === undefined || value === null || value === '') {
            return '-';
        }

        if (fieldName === 'dobirth' && value) {
            // Format date
            const date = new Date(value);
            return date.toLocaleDateString();
        }

        if (fieldName === 'custSex') {
            return value === 'M' ? 'Male' : value === 'F' ? 'Female' : value;
        }

        return value.toString();
    };

    // Product table columns
    const productColumns = [
        {
            title: 'Product',
            dataIndex: 'name',
            key: 'name'
        },
        {
            title: 'Type',
            dataIndex: 'type',
            key: 'type',
            render: (type: string) => (
                <Tag color={
                    type === 'S' ? 'blue' :
                        type === 'H' ? 'green' :
                            type === 'F' ? 'orange' :
                                type === 'L' ? 'red' : 'default'
                }>
                    {mapProductType(type)}
                </Tag>
            )
        },
        {
            title: 'GL Number',
            dataIndex: 'glNo',
            key: 'glNo'
        }
    ];

    return (
        <div className="space-y-6">
            <Card className={dark ? 'bg-gray-800' : ''}>
                <Title level={4} className={dark ? 'text-white' : ''}>
                    Customer Onboarding Summary
                </Title>

                <Divider className={dark ? 'border-gray-700' : ''} />

                <Row gutter={[24, 24]}>
                    {/* Customer Information */}
                    <Col span={24}>
                        <Descriptions
                            title={
                                <Text strong className={dark ? 'text-white' : ''}>
                                    Customer Information
                                </Text>
                            }
                            bordered
                            column={{ xxl: 4, xl: 3, lg: 3, md: 2, sm: 1, xs: 1 }}
                            className={dark ? 'descriptions-dark' : ''}
                        >
                            {getCustomerFields().map(([key, value]) => (
                                <Descriptions.Item
                                    key={key}
                                    label={getFieldLabel(key, 'Customer')}
                                >
                                    {formatFieldValue(value, key)}
                                </Descriptions.Item>
                            ))}
                        </Descriptions>
                    </Col>

                    {/* Additional Information */}
                    <Col span={24}>
                        <Descriptions
                            title={
                                <Text strong className={dark ? 'text-white' : ''}>
                                    Additional Information
                                </Text>
                            }
                            bordered
                            column={{ xxl: 4, xl: 3, lg: 3, md: 2, sm: 1, xs: 1 }}
                            className={dark ? 'descriptions-dark' : ''}
                        >
                            {getCustInfoFields().map(([key, value]) => (
                                <Descriptions.Item
                                    key={key}
                                    label={getFieldLabel(key, 'CustInfo')}
                                >
                                    {formatFieldValue(value, key)}
                                </Descriptions.Item>
                            ))}
                        </Descriptions>
                    </Col>

                    {/* Products */}
                    <Col span={24}>
                        <div className="mb-2">
                            <Text strong className={dark ? 'text-white' : ''}>
                                Selected Products
                            </Text>
                        </div>

                        <Table
                            dataSource={formData.selectedProducts}
                            columns={productColumns}
                            rowKey="glNo"
                            pagination={false}
                            size="small"
                            className={dark ? 'table-dark' : ''}
                        />
                    </Col>

                    {/* Category Info */}
                    <Col span={24}>
                        <div className="flex items-center mt-4">
                            <Badge
                                status={formData.isFreestyle ? 'warning' : 'success'}
                                text={
                                    <Text className={dark ? 'text-white' : ''}>
                                        {formData.isFreestyle
                                            ? 'Freestyle Mode'
                                            : `Category: ${category?.name || 'None'}`}
                                    </Text>
                                }
                            />
                        </div>
                    </Col>
                </Row>
            </Card>
        </div>
    );
};

export default SummaryStep;